import { type JSX, useEffect, useState } from "react";

import {
  ContentContainer,
  ContentHeader,
  PageTitle,
} from "features/layout/components";
import { LeadPreviewSidebar } from "features/leads/components";

import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import SearchInput from "common/components/SearchInput/SearchInput";
import { Button } from "common/components/ui/Button";
import useUnreadMessagesCount from "common/datahooks/useUnreadMessagesCount";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { ActiveConversationProvider } from "../context/ActiveConversationContext";
import {
  InboxEventsWebsocketProvider,
  useInboxEventsWebsocketContext,
} from "../context/InboxEventsWebsocketContext";
import useConversations from "../datahooks/useConversations";
import useActiveConversationState from "../hooks/useActiveConversationState";
import useInboxFilters from "../hooks/useInboxFilters";
import useKeywordsSearch from "../hooks/useKeywordsSearch";
import ConversationContainer from "./ConversationContainer/ConversationContainer";
import ConversationFilters from "./ConversationFilters";
import ConversationLoader from "./ConversationsList/ConversationLoader";
import ConversationsList from "./ConversationsList/ConversationsList";
import FilterConversations from "./FilterConversations/FilterConversations";
import InboxEmptyPlaceholder from "./InboxEmptyPlaceholder";
import MobileConversation from "./MobileConversation";
import NoResultsPlaceholder from "./NoResultsPlaceholder";

function Inbox() {
  const isDesktop = useTwBreakpoint("lg");

  const [isLeadPreviewOpen, setIsLeadPreviewOpen] = useState(false);
  const [leadPreviewId, setLeadPreviewId] = useState<string>();
  const {
    filteredBy,
    setFilteredBy,
    selectedFilters,
    setSelectedFilters,
    hasAnyFilters,
    clearAllFilters,
  } = useInboxFilters();
  const {
    keywords,
    keywordsInputValue,
    updateKeywordsInputValue,
    clearKeywords,
  } = useKeywordsSearch();

  const {
    conversations,
    isLoadingConversations,
    loadNextConversations,
    hasMoreConversations,
    hasConversations,
    refetchConversations,
    conversationsError,
    onUnreadMessage,
  } = useConversations({
    keywords,
    selectedAccountIds: selectedFilters.accountIds,
    selectedCampaignIds: selectedFilters.campaignIds,
    selectedLabelIds: selectedFilters.labelIds,
    filteredBy,
  });

  const { activeConversationState } = useActiveConversationState();
  const { resetUnreadMessages } = useUnreadMessagesCount();

  useInboxEventsWebsocketContext((data) => {
    if (data.type === "unread") {
      const { unread_count, refetch } = data;
      if (unread_count > 0) {
        resetUnreadMessages();
      }
      if (refetch) {
        onUnreadMessage();
      }
    }
  });

  useEffect(() => {
    if (isLeadPreviewOpen) {
      setIsLeadPreviewOpen(false);
    }
  }, [activeConversationState]);

  // Show error placeholder if there is an error fetching conversations
  if (conversationsError && !conversations) {
    return (
      <ContentContainer>
        <ContentHeader>
          <PageTitle />
        </ContentHeader>

        <ErrorPlaceholder
          onRetry={refetchConversations}
          errorMessage="Could not display Conversations"
        />
      </ContentContainer>
    );
  }

  // Check for showing empty state on the UI
  const shouldShowEmptyState =
    !conversations?.length &&
    !hasConversations &&
    !isLoadingConversations &&
    !hasAnyFilters &&
    !keywords;

  // Show empty state if there are no conversations and no filters are applied
  if (shouldShowEmptyState) {
    return <InboxEmptyPlaceholder />;
  }

  function showLeadPreview(leadId: string) {
    setIsLeadPreviewOpen(true);
    setLeadPreviewId(leadId);
  }

  const conversationFiltersComponent = (
    <FilterConversations
      activeFilters={selectedFilters}
      onSetActiveFilters={(filters) => {
        setSelectedFilters(filters);
        setIsLeadPreviewOpen(false);
      }}
    />
  );

  const searchInputComponent = (
    <SearchInput
      placeholder="Search conversations"
      className="w-full"
      value={keywordsInputValue}
      onChange={(e) => updateKeywordsInputValue(e.target.value)}
      onClear={clearKeywords}
      isLoading={keywords && isLoadingConversations}
    />
  );
  const readUnreadFilterComponent = (
    <ConversationFilters
      filteredBy={filteredBy}
      setFilteredBy={(filter) => {
        setIsLeadPreviewOpen(false);
        setFilteredBy(filter);
      }}
    />
  );

  const conversationComponent = (
    <ConversationContainer showLeadPreview={showLeadPreview} />
  );

  let conversationsListComponent: JSX.Element;

  if (isLoadingConversations || conversations?.length) {
    const isInitialLoading = isLoadingConversations && !conversations;

    let endComponent: JSX.Element;

    if (isLoadingConversations) {
      endComponent = <ConversationLoader itemsCount={3} />;
    } else if (conversationsError) {
      endComponent = (
        <div className="flex flex-col p-6 border-t border-t-black-200 items-center">
          <span className="mb-0.5 text-sm-bold">
            Could not load conversations
          </span>
          <p className="mb-4 text-xs-medium text-black-500">
            Something went wrong, please try again
          </p>
          <Button
            size="sm"
            variant="outline-black"
            onClick={() => refetchConversations()}
          >
            Load conversations
          </Button>
        </div>
      );
    }

    conversationsListComponent = isInitialLoading ? (
      <ConversationLoader itemsCount={10} />
    ) : (
      <ConversationsList
        conversations={conversations}
        canFetchNextPage={
          hasMoreConversations && !isLoadingConversations && !conversationsError
        }
        loadMoreConversations={loadNextConversations}
        endComponent={endComponent}
      />
    );
  } else {
    conversationsListComponent = (
      <NoResultsPlaceholder
        hasKeywords={!!keywords}
        hasOtherFilters={hasAnyFilters}
        clearFilters={() => {
          if (hasAnyFilters) {
            clearAllFilters();
          }
          clearKeywords();
        }}
      />
    );
  }

  return (
    <div className="flex gap-x-2 flex-grow min-h-0">
      <ContentContainer className="lg:max-w-[352px] lg:w-full">
        <ContentHeader>
          <PageTitle />
        </ContentHeader>
        <div className="flex flex-col gap-2.5 p-4">
          <div className="flex gap-2.5">
            {searchInputComponent}
            {conversationFiltersComponent}
          </div>
          {readUnreadFilterComponent}
        </div>

        {conversationsListComponent}
      </ContentContainer>
      <ActiveConversationProvider conversations={conversations}>
        {isDesktop ? (
          <>
            <ContentContainer>{conversationComponent}</ContentContainer>

            <LeadPreviewSidebar
              initialState={leadPreviewId}
              isOpen={isLeadPreviewOpen}
              onClose={() => setIsLeadPreviewOpen(false)}
              mode="inbox"
            />
          </>
        ) : (
          <MobileConversation>{conversationComponent}</MobileConversation>
        )}
      </ActiveConversationProvider>
    </div>
  );
}
export default function InboxContextWrapper() {
  return (
    <InboxEventsWebsocketProvider>
      <Inbox />
    </InboxEventsWebsocketProvider>
  );
}
